import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuAlertCircle, LuInfo } from 'react-icons/lu';

import {
   Card,
   CardHint,
   CardTitle,
   EmptyText,
   EmptyTitle,
   SkeletonBlock,
} from '../../../../styles/dunning/base';
import {
   SegmentedItem,
   SegmentedTrack,
} from '../../../../styles/dunning/filters';
import {
   AxisXLabel,
   AxisYLabel,
   BucketCard,
   BucketDot,
   BucketLabel,
   BucketRow,
   BucketSub,
   BucketUnit,
   BucketValue,
   BucketValueRow,
   ChartFootnote,
   ChartPad,
   ChartWrap,
   ClosedNote,
   HoverCell,
   HoverLayer,
   TimelineEmpty,
   TodayPill,
} from '../../../../styles/dunning/timeline';
import {
   CHART,
   TIMELINE_SPAN,
   buildTimelineModel,
   bucketOf,
   chartX,
   chartY,
} from '../utils/timeline';
import {
   addDays,
   formatBRL,
   formatBRLShort,
   formatDateBR,
   formatDayCount,
} from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Visão "Linha do tempo" (adendo 01) — análise de produtividade e demanda:
// quanto acompanhamento ficou para trás, quanto cai hoje e quanto vem pela
// frente. NÃO é visão de trabalho: não lista casos, não tem clique/drill-down
// (quem precisa agir vai para a Fila). Tudo derivado da resposta que já
// alimenta kanban/fila — trocar de visão ou de métrica não refaz request.
// ---------------------------------------------------------------------------

const METRIC_TABS = [
   { value: 'boletos', label: 'Boletos' },
   { value: 'casos', label: 'Casos' },
];

function unitLabel(value, isBoletos) {
   if (isBoletos) return value === 1 ? 'boleto' : 'boletos';
   return value === 1 ? 'caso' : 'casos';
}

function caseCountLabel(count, suffix = '') {
   return `${count} ${count === 1 ? `caso${suffix}` : `casos${suffix}`}`;
}

export default function TimelineView({
   rows,
   today,
   loading,
   metric,
   onMetricChange,
}) {
   const model = useMemo(
      () => buildTimelineModel({ rows, today, metric }),
      [rows, today, metric]
   );

   const {
      isBoletos,
      closedCount,
      hasChart,
      hasSem,
      series,
      buckets,
      sem,
      niceMax,
   } = model;

   const todayX = chartX(TIMELINE_SPAN);

   return (
      <Card>
         <div
            style={{
               padding: '14px 16px',
               borderBottom: '1px solid rgba(0,0,0,0.07)',
               display: 'flex',
               alignItems: 'center',
               gap: 12,
               flexWrap: 'wrap',
            }}
         >
            <div style={{ flex: 1, minWidth: 220 }}>
               <CardTitle>Linha do tempo de acompanhamento</CardTitle>
               <CardHint style={{ display: 'block', marginTop: 2 }}>
                  Compromissos de acompanhamento (
                  {isBoletos ? 'boletos' : 'casos'}) distribuídos em torno de
                  hoje · {formatDateBR(today)}
               </CardHint>
            </div>
            <SegmentedTrack>
               {METRIC_TABS.map((tab) => (
                  <SegmentedItem
                     key={tab.value}
                     type="button"
                     $active={metric === tab.value}
                     onClick={() => onMetricChange(tab.value)}
                  >
                     {tab.label}
                  </SegmentedItem>
               ))}
            </SegmentedTrack>
         </div>

         {loading && (
            <div style={{ padding: 16, display: 'grid', gap: 12 }}>
               <SkeletonBlock $h="72px" $radius="12px" />
               <SkeletonBlock $h="220px" $radius="12px" />
            </div>
         )}

         {!loading && !hasChart && (
            <TimelineEmpty>
               <EmptyTitle>Nenhum caso aberto no filtro atual</EmptyTitle>
               <EmptyText>
                  A linha do tempo só considera casos em cobrança.
               </EmptyText>
            </TimelineEmpty>
         )}

         {!loading && hasChart && (
            <>
               {/* ─── Faixa de baldes ────────────────────────────── */}
               <BucketRow>
                  {buckets.map((bucket) => {
                     let valueColor = '#111';
                     if (bucket.key === 'hoje') valueColor = '#0b3a63';
                     if (bucket.key === 'late7') valueColor = '#a01b1b';
                     return (
                        <BucketCard
                           key={bucket.key}
                           $tone={bucket.key === 'hoje' ? 'today' : undefined}
                        >
                           <BucketLabel>
                              <BucketDot $color={bucket.color} />
                              {bucket.label}
                           </BucketLabel>
                           <BucketValueRow>
                              <BucketValue $color={valueColor}>
                                 {bucket.value}
                              </BucketValue>
                              <BucketUnit>
                                 {isBoletos ? 'boletos' : 'casos'}
                              </BucketUnit>
                           </BucketValueRow>
                           <BucketSub>
                              {caseCountLabel(bucket.caseCount)} ·{' '}
                              {formatBRLShort(bucket.amount)}
                           </BucketSub>
                        </BucketCard>
                     );
                  })}

                  {hasSem && (
                     <BucketCard $tone="alert">
                        <BucketLabel $alert>
                           <LuAlertCircle size={14} />
                           Sem compromisso
                        </BucketLabel>
                        <BucketValueRow>
                           <BucketValue $color="#a01b1b">
                              {sem.value}
                           </BucketValue>
                           <BucketUnit>
                              {unitLabel(sem.value, isBoletos)}
                           </BucketUnit>
                        </BucketValueRow>
                        <BucketSub>
                           {caseCountLabel(sem.caseCount, ' sem data')} ·{' '}
                           {formatBRLShort(sem.amount)}
                        </BucketSub>
                     </BucketCard>
                  )}
               </BucketRow>

               {/* ─── Gráfico divergente ─────────────────────────── */}
               <ChartPad>
                  <ChartWrap>
                     <svg
                        viewBox={`0 0 ${CHART.viewW} ${CHART.viewH}`}
                        role="img"
                        aria-label="Distribuição dos compromissos em torno de hoje"
                     >
                        {[0, 0.5, 1].map((fraction) => (
                           <line
                              key={fraction}
                              x1={CHART.x0}
                              y1={chartY(niceMax * fraction, niceMax)}
                              x2={CHART.x1}
                              y2={chartY(niceMax * fraction, niceMax)}
                              stroke="rgba(0,0,0,0.07)"
                              strokeWidth="1"
                           />
                        ))}

                        <line
                           x1={todayX}
                           y1={CHART.topY}
                           x2={todayX}
                           y2={CHART.baseY}
                           stroke="#4098ef"
                           strokeWidth="1.5"
                           strokeDasharray="4 4"
                        />

                        {series
                           .filter((slot) => slot.value > 0)
                           .map((slot) => {
                              const bucket = bucketOf(slot.d);
                              const y = chartY(slot.value, niceMax);
                              return (
                                 <rect
                                    key={`bar-${slot.d}`}
                                    x={chartX(slot.d + TIMELINE_SPAN) - 7}
                                    y={y}
                                    width="14"
                                    height={CHART.baseY - y}
                                    rx="3"
                                    fill={bucket.color}
                                    opacity={slot.d === 0 ? 0.9 : 0.28}
                                 />
                              );
                           })}

                        {/* Passado e futuro compartilham o ponto de hoje;
                            dias sem compromisso valem 0 e a linha passa por
                            eles — a continuidade é o dado. */}
                        {[
                           {
                              key: 'past',
                              from: 0,
                              to: TIMELINE_SPAN + 1,
                              color: '#c2410c',
                           },
                           {
                              key: 'future',
                              from: TIMELINE_SPAN,
                              to: series.length,
                              color: '#7b8b99',
                           },
                        ].map((line) => (
                           <polyline
                              key={line.key}
                              points={series
                                 .slice(line.from, line.to)
                                 .map(
                                    (slot, k) =>
                                       `${chartX(line.from + k).toFixed(1)},${chartY(slot.value, niceMax).toFixed(1)}`
                                 )
                                 .join(' ')}
                              fill="none"
                              stroke={line.color}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                           />
                        ))}

                        {series
                           .filter((slot) => slot.value > 0)
                           .map((slot) => (
                              <circle
                                 key={`dot-${slot.d}`}
                                 cx={chartX(slot.d + TIMELINE_SPAN)}
                                 cy={chartY(slot.value, niceMax)}
                                 r={slot.d === 0 ? 6 : 4.5}
                                 fill="#fff"
                                 stroke={bucketOf(slot.d).color}
                                 strokeWidth="2.5"
                              />
                           ))}
                     </svg>

                     <TodayPill
                        style={{
                           left: `${((todayX / CHART.viewW) * 100).toFixed(2)}%`,
                        }}
                     >
                        Hoje
                     </TodayPill>

                     {[0, 0.5, 1].map((fraction) => (
                        <AxisYLabel
                           key={fraction}
                           style={{
                              top: `${((chartY(niceMax * fraction, niceMax) / CHART.viewH) * 100).toFixed(2)}%`,
                           }}
                        >
                           {Math.round(niceMax * fraction)}
                        </AxisYLabel>
                     ))}

                     {[
                        [0, '14 dias atrás'],
                        [7, '7 dias atrás'],
                        [14, 'Hoje'],
                        [21, 'em 7 dias'],
                        [28, 'em 14 dias'],
                     ].map(([index, label]) => (
                        <AxisXLabel
                           key={label}
                           $today={index === 14}
                           style={{
                              left: `${((chartX(index) / CHART.viewW) * 100).toFixed(2)}%`,
                           }}
                        >
                           {label}
                        </AxisXLabel>
                     ))}

                     <HoverLayer>
                        {series.map((slot) => {
                           let when = 'hoje';
                           if (slot.d < 0) {
                              when = `atrasado ${formatDayCount(-slot.d)}`;
                           } else if (slot.d > 0) {
                              when = `em ${formatDayCount(slot.d)}`;
                           }
                           const detail = slot.value
                              ? `${slot.value} ${unitLabel(slot.value, isBoletos)} · ${formatBRL(slot.amount)}`
                              : 'nada agendado';
                           return (
                              <HoverCell
                                 key={slot.d}
                                 $hasValue={slot.value > 0}
                                 title={`${formatDateBR(addDays(today, slot.d))} · ${when} · ${detail}`}
                              />
                           );
                        })}
                     </HoverLayer>
                  </ChartWrap>
               </ChartPad>

               <ChartFootnote>
                  Compromissos fora da janela de 14 dias são somados ao ponto
                  da borda. Casos sem compromisso não entram na linha — eles
                  exigem ação imediata.
               </ChartFootnote>
            </>
         )}

         {!loading && closedCount > 0 && (
            <ClosedNote>
               <LuInfo size={16} />
               {closedCount === 1
                  ? '1 caso encerrado no filtro atual não entra na linha — casos fechados não têm compromisso pendente.'
                  : `${closedCount} casos encerrados no filtro atual não entram na linha — casos fechados não têm compromisso pendente.`}
            </ClosedNote>
         )}
      </Card>
   );
}

TimelineView.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   rows: PropTypes.array.isRequired,
   today: PropTypes.string.isRequired,
   loading: PropTypes.bool.isRequired,
   metric: PropTypes.oneOf(['boletos', 'casos']).isRequired,
   onMetricChange: PropTypes.func.isRequired,
};
